import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import moment from "moment";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale";
import { createIniciativa, updateIniciativa } from "../../redux";
import { IniciativasIAServices, getIniciativaErrorCode } from "../../services/iniciativasIA";
import AvisoDevolucion from "./AvisoDevolucion";
import { makeSwalConfirmEscapeKeyDown } from "./swalEscapeGuard";

// RF-6/CA-8 (spec 009): mismo patrón de locale que TasksModalEdit.jsx.
registerLocale("es", es);

// RF-5 (spec 009): código fijo del catálogo T_VPMOLITE_INIA_CHECKLIST_ITEM para "Necesidad de revisión
// humana" (spec.md §1.1.9 de 009) — el ítem que se muestra siempre marcado y de solo lectura.
const CODIGO_REVISION_HUMANA = "REVISION_HUMANA";

// RF-17 (spec 009): código fijo del ítem "Uso de datos" — su "¿Cuáles?" (texto libre) se reemplaza por un
// selector de Tier (form.TierUsoDatos, columna propia de la iniciativa, no del checklist).
const CODIGO_USO_DATOS = "USO_DATOS";
// RF-17-bis (spec.md §6.5 de 009, decisión cerrada por Leonel): el Tier es obligatorio en el SP cuando
// USO_DATOS está marcado, y el selector nace SIEMPRE en Tier 0 (nunca vacío) — tanto en alta nueva como al
// precargar una iniciativa vieja con TierUsoDatos NULL (dev-seed de 008, p. ej. INI-2026-002). Es defensa
// deliberada frente a la validación pareja del SP (T-APP-15, no negociable): si el selector no nace en 0,
// cualquier edición de una fila vieja con USO_DATOS marcado revienta con CAMPOS_OBLIGATORIOS sin que el
// usuario entienda por qué.
const TIER_USO_DATOS_DEFAULT = "0";
const TIER_OPTIONS = [0, 1, 2, 3, 4];

// RF-6: helpers de formato, mismo criterio que TasksModalEdit.jsx (moment ya es dependencia del repo).
// El valor interno de form.Fecha sigue en "YYYY-MM-DD" (lo que ya consume el backend) — solo cambia el
// widget de entrada.
const parseYMD = (value) => (value ? moment(value, "YYYY-MM-DD").toDate() : null);
const formatDateToYMD = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

// Modal de registro/edición de la iniciativa (spec 008, RF-6..RF-13/RF-17, plan.md §3.4).
//
// T-SPIKE-1 (RESUELTO, tasks.md): todo <Modal> de react-bootstrap que aloje un swal() encima se declara
// con `enforceFocus={false}`.
//
// RF-10/CA-7/CA-8: sweetalert NO acepta HTML en `text` -> se arma el listado de faltantes como un nodo
// del DOM (document.createElement) y se pasa en la opción `content` (nunca dangerouslySetInnerHTML,
// R-14/H-05).

const emptyForm = {
  Nombre: "",
  Fecha: "",
  IdCluster: "",
  IdPostulante: "",
  IdClubhouse: "",
  Descripcion: "",
  Justificacion: "",
  Antecedentes: "",
  Beneficios: "",
  IdEstrategia: "",
  UsuariosBeneficiados: "",
  IdVigilancia: "",
  VigilanciaJustificacion: "",
  IdTipoIA: "",
  ResultadoEsperado: "",
  // RF-17/RF-17-bis (spec 009): nace en Tier 0 en alta nueva, no en vacío.
  TierUsoDatos: TIER_USO_DATOS_DEFAULT,
};

// Campos que el propio SP_INIA_CREAR/SP_INIA_ACTUALIZAR revalida (plan.md §8.4) — la UI solo asiste
// (RF-24), por eso la lista es idéntica a la del backend. El campo "Id iniciativa" NO entra aquí: lo
// asigna el sistema y siempre tiene un valor (RF-7).
const REQUIRED_FIELDS = [
  { key: "Nombre", labelKey: "iniciativasIA_field_name", sectionKey: "iniciativasIA_section_general" },
  { key: "Fecha", labelKey: "iniciativasIA_field_date", sectionKey: "iniciativasIA_section_general" },
  {
    key: "IdPostulante",
    labelKey: "iniciativasIA_field_applicant",
    sectionKey: "iniciativasIA_section_general",
  },
  {
    key: "IdClubhouse",
    labelKey: "iniciativasIA_field_clubhouse",
    sectionKey: "iniciativasIA_section_general",
  },
  { key: "IdTipoIA", labelKey: "iniciativasIA_field_ai_type", sectionKey: "iniciativasIA_section_definition" },
  {
    key: "ResultadoEsperado",
    labelKey: "iniciativasIA_field_expected_result",
    sectionKey: "iniciativasIA_section_definition",
  },
];

// Vista previa del código, calculada en el front a partir de las iniciativas ya cargadas — NO es
// autoritativa: el código real (a prueba de concurrencia, RF-26) lo asigna SP_INIA_CREAR en su propia
// transacción con UPDLOCK/HOLDLOCK (plan.md §1.6). Aquí solo satisface CA-6 (campo visible con el
// formato correcto antes de guardar); el valor persistido puede diferir si otro usuario registra al
// mismo tiempo, y es ese valor el que queda en la tabla tras el POST.
const nextCodigoPreview = (iniciativas) => {
  const year = new Date().getFullYear();
  const prefix = `INI-${year}-`;
  const last = (iniciativas || []).reduce((max, it) => {
    const code = String(it.Codigo || "");
    if (code.indexOf(prefix) !== 0) return max;
    const n = Number.parseInt(code.slice(prefix.length), 10);
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0);
  return `${prefix}${String(last + 1).padStart(3, "0")}`;
};

const IniciativaFormModal = ({ show, onHide, editingId, catalogos, iniciativas, onSaved }) => {
  const [t] = useTranslation("iniciativasIA");
  const dispatch = useDispatch();

  const [form, setForm] = useState(emptyForm);
  const [checklist, setChecklist] = useState({}); // { [IdChecklistItem]: { Marcado, Detalle } }
  const [invalidFields, setInvalidFields] = useState({});
  const [codigo, setCodigo] = useState("");
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  // RF-33/T-APP-21: historial + estado actual, para el aviso rojo de devolución (solo si CodigoEstado ===
  // "DEVUELTA"; se limpia en alta nueva y al reabrir para editar otra iniciativa).
  const [historial, setHistorial] = useState([]);
  const [codigoEstadoActual, setCodigoEstadoActual] = useState(null);

  const fieldRefs = useRef({});
  const setFieldRef = (key) => (el) => {
    fieldRefs.current[key] = el;
  };
  // RF-6: react-datepicker no forwarda un <input> nativo por ref (el ref apunta a la instancia del
  // componente, que expone setFocus()/setBlur(), no focus()). Se envuelve en un objeto mínimo con
  // .focus() para que el swal de "campos faltantes" (handleGuardarClick) lo pueda enfocar igual que a
  // cualquier otro campo del formulario, sin tocar ese mecanismo genérico.
  const setFechaRef = (instance) => {
    fieldRefs.current.Fecha = instance ? { focus: () => instance.setFocus() } : null;
  };

  // Memoizado a propósito: sin esto el `|| []` crea un arreglo nuevo en cada render y rompe la
  // estabilidad de referencia de los useMemo que dependen de él (revisionHumanaItem).
  const checklistItems = useMemo(() => catalogos?.checklistItems || [], [catalogos]);

  // RF-5 (spec 009): ítem "Necesidad de revisión humana", siempre marcado y de solo lectura.
  const revisionHumanaItem = useMemo(
    () => checklistItems.find((item) => item.Codigo === CODIGO_REVISION_HUMANA),
    [checklistItems]
  );

  // RF-17 (spec 009): ítem "Uso de datos" — su "¿Cuáles?" se reemplaza por el selector de Tier.
  const usoDatosItem = useMemo(
    () => checklistItems.find((item) => item.Codigo === CODIGO_USO_DATOS),
    [checklistItems]
  );
  const usoDatosMarcado = !!(usoDatosItem && checklist[usoDatosItem.IdChecklistItem]?.Marcado);

  const idVigilanciaSi = useMemo(() => {
    const found = (catalogos?.vigilancia || []).find((v) => v.Codigo === "SI");
    return found ? String(found.IdVigilancia) : null;
  }, [catalogos]);
  const isVigilanciaSi = idVigilanciaSi !== null && form.IdVigilancia === idVigilanciaSi;

  useEffect(() => {
    if (!show) return;
    setInvalidFields({});

    if (editingId) {
      setLoadingDetail(true);
      IniciativasIAServices.getIniciativa(editingId)
        .then((data) => {
          setForm({
            Nombre: data.Nombre || "",
            Fecha: data.Fecha ? String(data.Fecha).slice(0, 10) : "",
            IdCluster: data.IdCluster !== null && data.IdCluster !== undefined ? String(data.IdCluster) : "",
            IdPostulante: data.IdPostulante || "",
            IdClubhouse:
              data.IdClubhouse !== null && data.IdClubhouse !== undefined ? String(data.IdClubhouse) : "",
            Descripcion: data.Descripcion || "",
            Justificacion: data.Justificacion || "",
            Antecedentes: data.Antecedentes || "",
            Beneficios: data.Beneficios || "",
            IdEstrategia:
              data.IdEstrategia !== null && data.IdEstrategia !== undefined ? String(data.IdEstrategia) : "",
            UsuariosBeneficiados: data.UsuariosBeneficiados || "",
            IdVigilancia:
              data.IdVigilancia !== null && data.IdVigilancia !== undefined ? String(data.IdVigilancia) : "",
            VigilanciaJustificacion: data.VigilanciaJustificacion || "",
            IdTipoIA: data.IdTipoIA !== null && data.IdTipoIA !== undefined ? String(data.IdTipoIA) : "",
            ResultadoEsperado: data.ResultadoEsperado || "",
            // RF-17-bis (spec 009, T-APP-15 — no negociable): nace en Tier 0 también al precargar, sea
            // porque la iniciativa es nueva o porque es una fila vieja (dev-seed de 008) con
            // TierUsoDatos = NULL. Si trae un valor real guardado, se respeta ese valor.
            TierUsoDatos:
              data.TierUsoDatos !== null && data.TierUsoDatos !== undefined
                ? String(data.TierUsoDatos)
                : TIER_USO_DATOS_DEFAULT,
          });
          setCodigo(data.Codigo || "");
          const nextChecklist = {};
          (data.Checklist || []).forEach((row) => {
            nextChecklist[row.IdChecklistItem] = {
              Marcado: !!row.Marcado,
              Detalle: row.Detalle || "",
            };
          });
          // RF-5/CA-7: se fuerza a Marcado=true incluso si el dato guardado dice false (registros
          // creados antes de este spec) — no se deja pasar el valor histórico.
          if (revisionHumanaItem) {
            nextChecklist[revisionHumanaItem.IdChecklistItem] = {
              Marcado: true,
              Detalle: nextChecklist[revisionHumanaItem.IdChecklistItem]?.Detalle || "",
            };
          }
          setChecklist(nextChecklist);
          setHistorial(data.Historial || []);
          setCodigoEstadoActual(data.CodigoEstado || null);
        })
        .catch(() => {
          swal(t("iniciativasIA_swal_error_title"), t("iniciativasIA_swal_error_load_detail"), "error");
          onHide();
        })
        .finally(() => setLoadingDetail(false));
    } else {
      setForm(emptyForm);
      // RF-5/CA-7: en alta nueva el ítem nace marcado, no depende de que el usuario lo toque.
      setChecklist(
        revisionHumanaItem
          ? { [revisionHumanaItem.IdChecklistItem]: { Marcado: true, Detalle: "" } }
          : {}
      );
      setCodigo(nextCodigoPreview(iniciativas));
      setHistorial([]);
      setCodigoEstadoActual(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editingId]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setInvalidFields((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // RF-8/CA-9: el campo solo aplica cuando la respuesta es "Sí"; al cambiar a otra opción se oculta Y
  // se limpia (no basta con ocultarlo si conserva un valor viejo).
  const handleVigilanciaChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      IdVigilancia: value,
      VigilanciaJustificacion: value === idVigilanciaSi ? prev.VigilanciaJustificacion : "",
    }));
    setInvalidFields((prev) => {
      if (!prev.IdVigilancia && !prev.VigilanciaJustificacion) return prev;
      const next = { ...prev };
      delete next.IdVigilancia;
      if (value !== idVigilanciaSi) delete next.VigilanciaJustificacion;
      return next;
    });
  };

  // RF-9/CA-10: el "¿Cuáles?" permanece deshabilitado mientras la casilla esté sin marcar, y se limpia
  // al desmarcarla.
  const handleChecklistToggle = (itemId) => (e) => {
    const marcado = e.target.checked;
    setChecklist((prev) => ({
      ...prev,
      [itemId]: { Marcado: marcado, Detalle: marcado ? prev[itemId]?.Detalle || "" : "" },
    }));
    // RF-17/CA-22 (spec 009): "Uso de datos" ya no usa el "¿Cuáles?" del checklist -- usa TierUsoDatos,
    // un campo propio del formulario. Al desmarcar se limpia (mismo patrón que ya limpia
    // VigilanciaJustificacion, CA-9 de 008); al volver a marcar nace en Tier 0 si no tenía un valor ya
    // elegido (RF-17-bis).
    if (usoDatosItem && itemId === usoDatosItem.IdChecklistItem) {
      setField("TierUsoDatos", marcado ? form.TierUsoDatos || TIER_USO_DATOS_DEFAULT : "");
    }
    setInvalidFields((prev) => {
      const key = `checklist-${itemId}`;
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleChecklistDetalle = (itemId) => (e) => {
    const value = e.target.value;
    setChecklist((prev) => ({ ...prev, [itemId]: { ...prev[itemId], Detalle: value } }));
    setInvalidFields((prev) => {
      const key = `checklist-${itemId}`;
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getFaltantes = () => {
    const faltantes = REQUIRED_FIELDS.filter((f) => !String(form[f.key] || "").trim()).map((f) => ({
      key: f.key,
      label: t(f.labelKey),
      section: t(f.sectionKey),
    }));

    if (isVigilanciaSi && !form.VigilanciaJustificacion.trim()) {
      faltantes.push({
        key: "VigilanciaJustificacion",
        label: t("iniciativasIA_field_vigilancia_just"),
        section: t("iniciativasIA_section_benefits"),
      });
    }

    checklistItems
      .filter((item) => item.RequiereDetalle)
      .forEach((item) => {
        const entry = checklist[item.IdChecklistItem];
        if (entry?.Marcado && !String(entry.Detalle || "").trim()) {
          faltantes.push({
            key: `checklist-${item.IdChecklistItem}`,
            label: t("iniciativasIA_field_checklist_detail", { item: item.Nombre }),
            section: t("iniciativasIA_section_definition"),
          });
        }
      });

    return faltantes;
  };

  const buildPayload = () => ({
    Nombre: form.Nombre.trim(),
    Fecha: form.Fecha || null,
    IdCluster: form.IdCluster ? Number(form.IdCluster) : null,
    IdPostulante: form.IdPostulante || null,
    IdClubhouse: form.IdClubhouse ? Number(form.IdClubhouse) : null,
    Descripcion: form.Descripcion.trim() || null,
    Justificacion: form.Justificacion.trim() || null,
    Antecedentes: form.Antecedentes.trim() || null,
    Beneficios: form.Beneficios.trim() || null,
    IdEstrategia: form.IdEstrategia ? Number(form.IdEstrategia) : null,
    UsuariosBeneficiados: form.UsuariosBeneficiados.trim() || null,
    IdVigilancia: form.IdVigilancia ? Number(form.IdVigilancia) : null,
    VigilanciaJustificacion: isVigilanciaSi ? form.VigilanciaJustificacion.trim() || null : null,
    IdTipoIA: form.IdTipoIA ? Number(form.IdTipoIA) : null,
    ResultadoEsperado: form.ResultadoEsperado.trim() || null,
    // RF-17: solo se envía si el ítem "Uso de datos" está marcado; nunca vacío en ese caso (nace en 0,
    // RF-17-bis).
    TierUsoDatos: usoDatosMarcado ? Number(form.TierUsoDatos || TIER_USO_DATOS_DEFAULT) : null,
    Checklist: checklistItems.map((item) => ({
      IdChecklistItem: item.IdChecklistItem,
      Marcado: !!checklist[item.IdChecklistItem]?.Marcado,
      Detalle: (checklist[item.IdChecklistItem]?.Detalle || "").trim() || null,
    })),
  });

  const doGuardar = () => {
    setSaving(true);
    const payload = buildPayload();
    const action = editingId ? updateIniciativa(editingId, payload) : createIniciativa(payload);
    dispatch(action)
      .then(() => {
        setSaving(false);
        onSaved();
        swal({
          title: t("iniciativasIA_swal_saved_title"),
          // El `text` no es decorativo: sin él el título es :last-child y SweetAlert le deja
          // padding-bottom:0, así que queda pegado al borde inferior y el aviso se ve cortado.
          text: t("iniciativasIA_swal_saved_text"),
          icon: "success",
          timer: 1600,
          buttons: false,
        });
      })
      .catch((err) => {
        setSaving(false);
        const code = getIniciativaErrorCode(err);
        if (code === "ESTADO_NO_EDITABLE") {
          swal(t("iniciativasIA_swal_error_title"), t("iniciativasIA_swal_error_not_editable"), "error");
        } else if (code === "CAMPOS_OBLIGATORIOS") {
          swal(t("iniciativasIA_swal_error_title"), t("iniciativasIA_swal_error_missing_backend"), "error");
        } else {
          swal(t("iniciativasIA_swal_error_title"), t("iniciativasIA_swal_error_generic"), "error");
        }
      });
  };

  // RF-10/CA-7/CA-8: SweetAlert con los faltantes agrupados por sección. `sweetalert` no acepta HTML en
  // `text`, así que el listado se arma como un nodo del DOM y se pasa en `content` (nunca
  // dangerouslySetInnerHTML).
  const handleGuardarClick = () => {
    const faltantes = getFaltantes();
    if (faltantes.length) {
      const invalidMap = {};
      faltantes.forEach((f) => {
        invalidMap[f.key] = true;
      });
      setInvalidFields(invalidMap);

      const porSeccion = {};
      faltantes.forEach((f) => {
        (porSeccion[f.section] = porSeccion[f.section] || []).push(f.label);
      });

      const container = document.createElement("div");
      container.style.textAlign = "left";

      const intro = document.createElement("p");
      intro.className = "mb-2";
      intro.textContent =
        faltantes.length === 1
          ? t("iniciativasIA_swal_missing_intro_one")
          : t("iniciativasIA_swal_missing_intro_many", { count: faltantes.length });
      container.appendChild(intro);

      Object.keys(porSeccion).forEach((section) => {
        const h = document.createElement("p");
        h.className = "mb-1";
        const b = document.createElement("b");
        b.textContent = section;
        h.appendChild(b);
        container.appendChild(h);

        const ul = document.createElement("ul");
        ul.className = "mb-2";
        porSeccion[section].forEach((label) => {
          const li = document.createElement("li");
          li.textContent = label;
          ul.appendChild(li);
        });
        container.appendChild(ul);
      });

      swal({
        title: t("iniciativasIA_swal_missing_title"),
        icon: "warning",
        content: container,
        button: t("iniciativasIA_swal_btn_understood"),
      }).then(() => {
        const first = fieldRefs.current[faltantes[0].key];
        if (first) {
          first.focus();
          if (first.scrollIntoView) first.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      });
      return;
    }

    swal({
      title: t("iniciativasIA_swal_save_title"),
      text: editingId ? t("iniciativasIA_swal_save_text_edit") : t("iniciativasIA_swal_save_text_new"),
      icon: "info",
      buttons: [t("iniciativasIA_swal_btn_no"), t("iniciativasIA_swal_btn_save")],
    }).then((confirmado) => {
      if (confirmado) doGuardar();
    });
  };

  // RF-12/CA-12: Cancelar no persiste nada y pide confirmación previa. Se usa como `onHide` del propio
  // <Modal> para que la X, el backdrop y el botón "Cancelar" pasen siempre por la misma confirmación
  // (RF-21: toda acción se confirma).
  const handleRequestClose = () => {
    if (saving) return;
    swal({
      title: t("iniciativasIA_swal_cancel_title"),
      text: t("iniciativasIA_swal_cancel_text"),
      icon: "warning",
      dangerMode: true,
      buttons: [t("iniciativasIA_swal_btn_no"), t("iniciativasIA_swal_btn_yes_exit")],
    }).then((confirmado) => {
      if (confirmado) onHide();
    });
  };

  const invalidClass = (key) => (invalidFields[key] ? " is-invalid" : "");

  return (
    <Modal
      show={show}
      onHide={handleRequestClose}
      onEscapeKeyDown={makeSwalConfirmEscapeKeyDown(handleRequestClose)}
      size="xl"
      enforceFocus={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          <i className="fa fa-brain mr-2 text-primary"></i>
          {editingId ? t("iniciativasIA_modal_title_edit") : t("iniciativasIA_modal_title_new")}
        </Modal.Title>
        {/* No se usa `closeButton` de react-bootstrap: en la v2 renderiza `.btn-close`, que es de
            Bootstrap 5 y NO existe en el CSS 4.6 que realmente compila la app (DT-3) — el botón queda
            invisible. Se usa la clase `.close` de BS4, que sí está en custom.css y trae el estilo y la
            posición correctos. `&times;` (×) es el glifo, no la letra X. */}
        <button
          type="button"
          className="close"
          aria-label={t("iniciativasIA_modal_close_aria")}
          onClick={handleRequestClose}
          disabled={saving}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </Modal.Header>
      <Modal.Body>
        {loadingDetail ? (
          <div className="text-center py-5">
            <i className="fa fa-spinner fa-spin fa-2x text-primary"></i>
          </div>
        ) : (
          <>
            <AvisoDevolucion historial={historial} visible={codigoEstadoActual === "DEVUELTA"} />

            {/* ---- Sección 1 — Información General ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-id-card mr-2"></i>
                {t("iniciativasIA_section_general")}
              </h6>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="brand-required">{t("iniciativasIA_field_id")}</label>
                  <input type="text" className="form-control" value={codigo} readOnly />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="brand-required">{t("iniciativasIA_field_name")}</label>
                  <input
                    ref={setFieldRef("Nombre")}
                    type="text"
                    className={`form-control${invalidClass("Nombre")}`}
                    placeholder={t("iniciativasIA_placeholder_name")}
                    value={form.Nombre}
                    onChange={(e) => setField("Nombre", e.target.value)}
                  />
                </div>
                <div className="col-md-3 mb-3">
                  <label className="brand-required">{t("iniciativasIA_field_date")}</label>
                  <DatePicker
                    ref={setFechaRef}
                    selected={parseYMD(form.Fecha)}
                    onChange={(date) => setField("Fecha", date ? formatDateToYMD(date) : "")}
                    dateFormat="dd/MM/yyyy"
                    locale={window.localStorage.getItem("idioma") || "es"}
                    className={`form-control${invalidClass("Fecha")}`}
                    wrapperClassName="w-100"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label>{t("iniciativasIA_field_cluster")}</label>
                  <select
                    className="form-control"
                    value={form.IdCluster}
                    onChange={(e) => setField("IdCluster", e.target.value)}
                  >
                    <option value="">{t("iniciativasIA_select_placeholder")}</option>
                    {(catalogos?.clusters || []).map((c) => (
                      <option key={c.IdCluster} value={c.IdCluster}>
                        {c.Nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="brand-required">{t("iniciativasIA_field_applicant")}</label>
                  <select
                    ref={setFieldRef("IdPostulante")}
                    className={`form-control${invalidClass("IdPostulante")}`}
                    value={form.IdPostulante}
                    onChange={(e) => setField("IdPostulante", e.target.value)}
                  >
                    <option value="">{t("iniciativasIA_select_placeholder")}</option>
                    {(catalogos?.usuarios || []).map((u) => (
                      <option key={u.IdUsuario} value={u.IdUsuario}>
                        {u.NombreCompleto}
                      </option>
                    ))}
                  </select>
                </div>
                {/* RF-3/CA-4 (spec 009): campo Sponsor eliminado del formulario (queda solo de solo
                    lectura en IniciativaDetalleModal, RF-3/CA-5). No hay obligatoriedad de backend que
                    tocar (spec.md §1.1.6 de 009). */}
                <div className="col-md-3 mb-3">
                  <label className="brand-required">{t("iniciativasIA_field_clubhouse")}</label>
                  <select
                    ref={setFieldRef("IdClubhouse")}
                    className={`form-control${invalidClass("IdClubhouse")}`}
                    value={form.IdClubhouse}
                    onChange={(e) => setField("IdClubhouse", e.target.value)}
                  >
                    <option value="">{t("iniciativasIA_select_placeholder")}</option>
                    {(catalogos?.clubhouses || []).map((c) => (
                      <option key={c.IdClubhouse} value={c.IdClubhouse}>
                        {c.Nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* ---- Sección 2 — Detalle de la Iniciativa ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-align-left mr-2"></i>
                {t("iniciativasIA_section_detail")}
              </h6>
              <div className="mb-3">
                <label>{t("iniciativasIA_field_description")}</label>
                <textarea
                  rows={3}
                  className="form-control"
                  placeholder={t("iniciativasIA_placeholder_description")}
                  value={form.Descripcion}
                  onChange={(e) => setField("Descripcion", e.target.value)}
                ></textarea>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>{t("iniciativasIA_field_justification")}</label>
                  <textarea
                    rows={4}
                    className="form-control"
                    placeholder={t("iniciativasIA_placeholder_justification")}
                    value={form.Justificacion}
                    onChange={(e) => setField("Justificacion", e.target.value)}
                  ></textarea>
                </div>
                <div className="col-md-6 mb-3">
                  <label>{t("iniciativasIA_field_background")}</label>
                  <textarea
                    rows={4}
                    className="form-control"
                    placeholder={t("iniciativasIA_placeholder_background")}
                    value={form.Antecedentes}
                    onChange={(e) => setField("Antecedentes", e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* ---- Sección 3 — Beneficios ---- */}
            <div className="mb-4">
              <h6>
                <i className="fa fa-hand-holding-usd mr-2"></i>
                {t("iniciativasIA_section_benefits")}
              </h6>
              {/* Los campos se agrupan por TIPO de control, no por orden de aparición en el documento
                  fuente: primero los 2 textareas juntos y después los 2 selects juntos. Mezclarlos en una
                  misma fila (como estaba: select / textarea / select) deja los selects flotando arriba
                  con un hueco muerto del alto del textarea. Mismo patrón 6/6 que ya usa la Sección 2
                  (Justificación | Antecedentes). */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label>{t("iniciativasIA_field_benefits")}</label>
                  <textarea
                    rows={3}
                    className="form-control"
                    placeholder={t("iniciativasIA_placeholder_benefits")}
                    value={form.Beneficios}
                    onChange={(e) => setField("Beneficios", e.target.value)}
                  ></textarea>
                </div>
                <div className="col-md-6 mb-3">
                  <label>{t("iniciativasIA_field_beneficiary_users")}</label>
                  <textarea
                    rows={3}
                    className="form-control"
                    placeholder={t("iniciativasIA_placeholder_beneficiary_users")}
                    value={form.UsuariosBeneficiados}
                    onChange={(e) => setField("UsuariosBeneficiados", e.target.value)}
                  ></textarea>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  {/* RF-16/CA-21 (spec 009): "Alineación estratégica" pasa de texto libre a un <select>
                      cargado del catálogo T_VPMOLITE_INIA_ESTRATEGIA de la organización (resuelto por
                      idioma en el backend, nunca quemado en el front). */}
                  <label>{t("iniciativasIA_field_strategic_alignment")}</label>
                  <select
                    className="form-control"
                    value={form.IdEstrategia}
                    onChange={(e) => setField("IdEstrategia", e.target.value)}
                  >
                    <option value="">{t("iniciativasIA_select_placeholder")}</option>
                    {(catalogos?.estrategias || []).map((estrategia) => (
                      <option key={estrategia.IdEstrategia} value={estrategia.IdEstrategia}>
                        {estrategia.Nombre}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Vigilancia queda pegada a su justificación condicional, que se despliega justo debajo
                    al elegir "Sí" — antes las separaba el textarea de usuarios beneficiados. */}
                <div className="col-md-6 mb-3">
                  <label>{t("iniciativasIA_field_vigilancia")}</label>
                  <select
                    className="form-control"
                    value={form.IdVigilancia}
                    onChange={handleVigilanciaChange}
                  >
                    <option value="">{t("iniciativasIA_select_placeholder")}</option>
                    {(catalogos?.vigilancia || []).map((v) => (
                      <option key={v.IdVigilancia} value={v.IdVigilancia}>
                        {v.Nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {isVigilanciaSi && (
                <div className="mb-0">
                  <label className="brand-required">{t("iniciativasIA_field_vigilancia_just")}</label>
                  <textarea
                    ref={setFieldRef("VigilanciaJustificacion")}
                    rows={3}
                    className={`form-control${invalidClass("VigilanciaJustificacion")}`}
                    placeholder={t("iniciativasIA_placeholder_vigilancia_just")}
                    value={form.VigilanciaJustificacion}
                    onChange={(e) => setField("VigilanciaJustificacion", e.target.value)}
                  ></textarea>
                </div>
              )}
            </div>

            {/* ---- Sección 4 — Definición ---- */}
            <div className="mb-0">
              <h6>
                <i className="fa fa-microchip mr-2"></i>
                {t("iniciativasIA_section_definition")}
              </h6>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="brand-required">{t("iniciativasIA_field_ai_type")}</label>
                  <select
                    ref={setFieldRef("IdTipoIA")}
                    className={`form-control${invalidClass("IdTipoIA")}`}
                    value={form.IdTipoIA}
                    onChange={(e) => setField("IdTipoIA", e.target.value)}
                  >
                    <option value="">{t("iniciativasIA_select_placeholder")}</option>
                    {(catalogos?.tiposIA || []).map((tipo) => (
                      <option key={tipo.IdTipoIA} value={tipo.IdTipoIA}>
                        {tipo.Nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <table className="table table-bordered checklist-ia mb-0">
                  <caption className="checklist-ia-caption">
                    {t("iniciativasIA_checklist_title")}
                  </caption>
                  <tbody>
                    {checklistItems.map((item) => {
                      const entry = checklist[item.IdChecklistItem] || { Marcado: false, Detalle: "" };
                      const key = `checklist-${item.IdChecklistItem}`;
                      // RF-5/CA-7 (spec 009): "Necesidad de revisión humana" siempre marcado y de solo
                      // lectura — no usa el mecanismo genérico de RequiereDetalle/Marcado editable.
                      const isRevisionHumana = item.Codigo === CODIGO_REVISION_HUMANA;
                      // RF-17/CA-22 (spec 009): "Uso de datos" ya no usa "¿Cuáles?" (RequiereDetalle pasó
                      // a 0 en el catálogo, U002) — usa el selector de Tier, siempre explícito por Codigo
                      // (no depende únicamente del dato de catálogo).
                      const isUsoDatos = item.Codigo === CODIGO_USO_DATOS;
                      return (
                        <tr key={item.IdChecklistItem}>
                          <td>{item.Nombre}</td>
                          <td className="text-center" style={{ width: "60px" }}>
                            <input
                              type="checkbox"
                              checked={isRevisionHumana ? true : !!entry.Marcado}
                              disabled={isRevisionHumana}
                              onChange={isRevisionHumana ? undefined : handleChecklistToggle(item.IdChecklistItem)}
                            />
                          </td>
                          <td>
                            {isRevisionHumana ? (
                              // RF-5/CA-7: texto de concientización, de solo lectura (i18n, nunca un
                              // string quemado ni dangerouslySetInnerHTML).
                              <textarea
                                rows={3}
                                disabled
                                className="form-control form-control-sm"
                                value={t("iniciativasIA_revision_humana_mensaje")}
                                readOnly
                              ></textarea>
                            ) : isUsoDatos ? (
                              // RF-17/CA-22: selector de Tier 0..4, solo visible mientras el ítem está
                              // marcado (mismo criterio de visibilidad condicional que el resto del
                              // checklist). Etiquetas i18n del FRONT (constante, no catálogo de BD —
                              // decisión cerrada de Leonel, spec.md §6.6/RF-17).
                              entry.Marcado && (
                                <>
                                  <label className="brand-required mb-0 small d-block">
                                    {t("iniciativasIA_field_tier_datos")}
                                  </label>
                                  <select
                                    ref={setFieldRef("TierUsoDatos")}
                                    className="form-control form-control-sm"
                                    value={form.TierUsoDatos || TIER_USO_DATOS_DEFAULT}
                                    onChange={(e) => setField("TierUsoDatos", e.target.value)}
                                  >
                                    {TIER_OPTIONS.map((tier) => (
                                      <option key={tier} value={tier}>
                                        {t(`iniciativasIA_tier_${tier}`)}
                                      </option>
                                    ))}
                                  </select>
                                </>
                              )
                            ) : item.RequiereDetalle ? (
                              <>
                                <label className="brand-required mb-0 small d-block">
                                  {t("iniciativasIA_checklist_which")}
                                </label>
                                {/* RF-4/CA-6: textarea multilínea para todo ítem con RequiereDetalle,
                                    genérico (no por Codigo). */}
                                <textarea
                                  ref={setFieldRef(key)}
                                  rows={3}
                                  className={`form-control form-control-sm${invalidClass(key)}`}
                                  value={entry.Detalle}
                                  disabled={!entry.Marcado}
                                  onChange={handleChecklistDetalle(item.IdChecklistItem)}
                                ></textarea>
                              </>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mb-0">
                <label className="brand-required">{t("iniciativasIA_field_expected_result")}</label>
                <textarea
                  ref={setFieldRef("ResultadoEsperado")}
                  rows={3}
                  className={`form-control${invalidClass("ResultadoEsperado")}`}
                  placeholder={t("iniciativasIA_placeholder_expected_result")}
                  value={form.ResultadoEsperado}
                  onChange={(e) => setField("ResultadoEsperado", e.target.value)}
                ></textarea>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-dark" onClick={handleRequestClose} disabled={saving}>
          <i className="fa fa-times mr-2"></i>
          {t("iniciativasIA_btn_cancel")}
        </button>
        <button
          type="button"
          className="btn estado-guardado"
          onClick={handleGuardarClick}
          disabled={saving || loadingDetail}
        >
          <i className="fa fa-save mr-2"></i>
          {t("iniciativasIA_btn_save")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default IniciativaFormModal;
